import { Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { money } from "@/lib/api";
import ComplianceBanner from "@/components/ComplianceBanner";
import { Trash, ArrowRight } from "@phosphor-icons/react";

export default function Cart() {
  const { items, update, remove, subtotal_cents } = useCart();
  const shipping = items.length === 0 ? 0 : subtotal_cents >= 15000 ? 0 : 1200;
  const total = subtotal_cents + shipping;

  return (
    <div className="page-enter">
      <ComplianceBanner />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="overline mb-3">Cart</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Review your order</h1>

          {items.length === 0 ? (
            <div className="mt-10 border border-dashed border-black/20 p-16 text-center">
              <div className="overline mb-2">Empty cart</div>
              <p className="text-zinc-600">Browse the catalog to add research materials.</p>
              <Link to="/shop" className="btn-outline mt-6 inline-flex">Browse catalog</Link>
            </div>
          ) : (
            <div className="mt-8 border border-black/10 divide-y divide-black/10">
              {items.map((i) => (
                <div key={i.key} className="p-4 md:p-6 flex gap-4 items-start" data-testid={`cart-item-${i.product_slug}`}>
                  <div className="w-24 h-24 bg-zinc-100 border border-black/10 overflow-hidden shrink-0">
                    {i.image_url && <img src={i.image_url} alt={i.product_name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <Link to={`/product/${i.product_slug}`} className="font-display font-semibold link-underline">{i.product_name}</Link>
                    {i.variant_label && <div className="text-xs font-mono text-zinc-500 mt-1">{i.variant_label} · {i.variant_sku}</div>}
                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex items-center border border-black/20">
                        <button className="px-2 py-1" onClick={() => update(i.key, Math.max(1, i.quantity - 1))}>-</button>
                        <span className="w-8 text-center font-mono text-sm">{i.quantity}</span>
                        <button className="px-2 py-1" onClick={() => update(i.key, Math.min(99, i.quantity + 1))}>+</button>
                      </div>
                      <button onClick={() => remove(i.key)} className="text-sm text-zinc-500 hover:text-black inline-flex items-center gap-1" data-testid={`remove-${i.product_slug}`}>
                        <Trash size={14} /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono">{money(i.unit_price_cents * i.quantity)}</div>
                    <div className="text-xs text-zinc-500 mt-1">{money(i.unit_price_cents)} ea.</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="border border-black/10 p-6 sticky top-24">
            <div className="overline mb-3">Summary</div>
            <dl>
              <div className="data-row"><dt>Subtotal</dt><dd>{money(subtotal_cents)}</dd></div>
              <div className="data-row"><dt>Shipping</dt><dd>{items.length === 0 ? "—" : shipping === 0 ? "Free" : money(shipping)}</dd></div>
              <div className="data-row"><dt>Tax</dt><dd>Calculated at checkout</dd></div>
              <div className="data-row"><dt><b>Total</b></dt><dd><b>{money(total)}</b></dd></div>
            </dl>
            <Link to="/checkout" className={`btn-prm w-full mt-6 ${items.length === 0 ? "opacity-40 pointer-events-none" : ""}`} data-testid="cart-checkout">
              Continue to checkout <ArrowRight size={16} />
            </Link>
            <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
              Orders are held pending payment. Live payment integration is not enabled in this environment.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
